import React, { Component } from "react";
import SEO from "react-seo-component";
import { Col, Container, Row, Card, CardBody } from "reactstrap";

/**
 * QikTruck Cancellation Policy (South Africa)
 * Applies to Customers, Drivers, and QikTruck Staff
 * Notes:
 * - This is a practical, SA-aligned cancellation policy suitable for a logistics marketplace.
 * - Designed to be legally defensible in South Africa, fair in a marketplace context, and easy to apply operationally.
 */

const styles = {
  heroSection: {
    background: 'linear-gradient(135deg, #FF6B00 0%, #FF8533 100%)',
    padding: '40px 15px 30px',
    marginBottom: '30px',
    color: 'white',
  },
  heroTitle: {
    fontSize: 'clamp(1.5rem, 5vw, 2.5rem)',
    fontWeight: '700',
    marginBottom: '12px',
    lineHeight: '1.2',
  },
  heroSubtitle: {
    fontSize: 'clamp(0.9rem, 3vw, 1.1rem)',
    opacity: '0.95',
    marginBottom: '10px',
    lineHeight: '1.5',
  },
  lastUpdated: {
    fontSize: '0.85rem',
    opacity: '0.8',
  },
  quickSummary: {
    background: '#f8f9fa',
    borderRadius: '12px',
    padding: '15px 12px',
    marginBottom: '25px',
    border: '1px solid #e9ecef',
  },
  summaryTitle: {
    fontSize: 'clamp(1rem, 4vw, 1.3rem)',
    fontWeight: '600',
    marginBottom: '12px',
    color: '#333',
    textAlign: 'center',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '10px',
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '6px',
    padding: '12px 8px',
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  },
  summaryIcon: {
    fontSize: 'clamp(1.3rem, 4vw, 1.8rem)',
    lineHeight: '1',
    flexShrink: 0,
  },
  summaryText: {
    fontSize: 'clamp(0.75rem, 2.5vw, 0.85rem)',
    color: '#555',
    margin: 0,
    lineHeight: '1.4',
  },
  summaryLabel: {
    fontWeight: '600',
    color: '#333',
    display: 'block',
    marginBottom: '3px',
    fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)',
  },
  feeTable: {
    width: '100%',
    borderCollapse: 'collapse',
    borderSpacing: '0',
    marginBottom: '0',
    tableLayout: 'fixed',
  },
  feeTableHeader: {
    background: '#FF6B00',
    color: 'white',
    padding: '10px 12px',
    textAlign: 'left',
    fontWeight: '600',
    fontSize: 'clamp(0.75rem, 2.5vw, 0.9rem)',
  },
  feeTableCell: {
    padding: '10px 12px',
    borderBottom: '1px solid #eee',
    verticalAlign: 'middle',
    fontSize: 'clamp(0.75rem, 2.5vw, 0.9rem)',
    lineHeight: '1.4',
    wordWrap: 'break-word',
  },
  feeTableCellAlt: {
    padding: '10px 12px',
    borderBottom: '1px solid #eee',
    background: '#fafafa',
    verticalAlign: 'middle',
    fontSize: 'clamp(0.75rem, 2.5vw, 0.9rem)',
    lineHeight: '1.4',
    wordWrap: 'break-word',
  },
  feeBadge: {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: '12px',
    fontSize: 'clamp(0.7rem, 2vw, 0.85rem)',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  feeBadgeGreen: {
    background: '#e8f5e9',
    color: '#2e7d32',
  },
  feeBadgeYellow: {
    background: '#fff3e0',
    color: '#e65100',
  },
  feeBadgeOrange: {
    background: '#fff3e0',
    color: '#e65100',
  },
  feeBadgeRed: {
    background: '#ffebee',
    color: '#c62828',
  },
  feeBadgeFree: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: '600',
    background: '#e8f5e9',
    color: '#2e7d32',
  },
  feeBadgeLow: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: '600',
    background: '#fff3e0',
    color: '#e65100',
  },
  feeBadgeMedium: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: '600',
    background: '#fff3e0',
    color: '#e65100',
  },
  feeBadgeHigh: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: '600',
    background: '#ffebee',
    color: '#c62828',
  },
  sectionCard: {
    marginBottom: '15px',
    borderRadius: '10px',
    border: '1px solid #e9ecef',
    overflow: 'hidden',
  },
  sectionHeader: {
    background: '#f8f9fa',
    padding: '12px',
    borderBottom: '1px solid #e9ecef',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sectionNumber: {
    width: '26px',
    height: '26px',
    minWidth: '26px',
    borderRadius: '50%',
    background: '#FF6B00',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: 'clamp(0.75rem, 2.5vw, 0.85rem)',
  },
  sectionTitle: {
    margin: 0,
    fontSize: 'clamp(0.9rem, 3.5vw, 1.15rem)',
    fontWeight: '600',
    color: '#333',
    lineHeight: '1.25',
    flex: 1,
  },
  sectionBody: {
    padding: '12px',
  },
  infoBox: {
    background: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderLeft: '3px solid #FF6B00',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '12px',
    display: 'block',
    fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)',
    lineHeight: '1.5',
  },
  infoBoxIcon: {
    fontSize: '1.1rem',
    color: '#FF6B00',
  },
  warningBox: {
    background: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderLeft: '3px solid #FF6B00',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '12px',
    display: 'block',
    fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)',
    lineHeight: '1.5',
  },
  successBox: {
    background: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderLeft: '3px solid #FF6B00',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '12px',
    display: 'block',
    fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)',
    lineHeight: '1.5',
  },
  tocList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '6px',
  },
  tocItem: {
    padding: '8px 6px',
    background: 'white',
    borderRadius: '6px',
    border: '1px solid #e9ecef',
    display: 'block',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textDecoration: 'none',
    color: '#333',
    fontSize: 'clamp(0.7rem, 2.2vw, 0.85rem)',
    lineHeight: '1.3',
    textAlign: 'center',
  },
  contentParagraph: {
    fontSize: 'clamp(0.85rem, 2.8vw, 0.95rem)',
    lineHeight: '1.6',
    color: '#444',
    marginBottom: '12px',
  },
  contentList: {
    paddingLeft: '18px',
    listStyle: 'disc',
    margin: '8px 0',
  },
  contentListItem: {
    marginBottom: '8px',
    fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)',
    lineHeight: '1.5',
    color: '#444',
  },
};

class Section extends Component {
  render() {

    return (
      <React.Fragment>
        <SEO
          title={
            "QikTruck Cancellation Policy - Booking cancellation fees and rules for customers and drivers"
          }
          titleTemplate={"QikTruck"}
          titleSeparator={"-"}
          description={
            "QikTruck Cancellation Policy explains when a booking becomes binding, when cancellation fees apply, how fees are calculated, and why they exist. Fair rules for customers, drivers, and staff."
          }
          image={"https://media.qiktruck.co.za/qiktruck-preview-header.png"}
          pathname={"https://qiktruck.co.za/cancellation-policy"}
          twitterUsername={"qiktrucksa"}
        />

        {/* Hero Section */}
        <div style={styles.heroSection}>
          <Container>
            <Row className="justify-content-center">
              <Col lg={10}>
                <h1 style={styles.heroTitle}>Cancellation Policy</h1>
                <p style={styles.heroSubtitle}>
                  Clear, fair, and transparent guidelines for customers, drivers, and staff
                </p>
              </Col>
            </Row>
          </Container>
        </div>

        <section id="home">
          <Container>
            <Row className="justify-content-center">
              <Col lg={10}>
                
                {/* Quick Summary */}
                <div style={styles.quickSummary}>
                  <h2 style={styles.summaryTitle}>📋 Quick Summary</h2>
                  <div style={styles.summaryGrid}>
                    <div style={styles.summaryItem}>
                      <span style={styles.summaryIcon}>✅</span>
                      <p style={styles.summaryText}>
                        <span style={styles.summaryLabel}>Quotes are free</span>
                        No fee for requesting or declining quotes
                      </p>
                    </div>
                    <div style={styles.summaryItem}>
                      <span style={styles.summaryIcon}>🕐</span>
                      <p style={styles.summaryText}>
                        <span style={styles.summaryLabel}>24+ hours notice</span>
                        No cancellation fee applies
                      </p>
                    </div>
                    <div style={styles.summaryItem}>
                      <span style={styles.summaryIcon}>⚠️</span>
                      <p style={styles.summaryText}>
                        <span style={styles.summaryLabel}>4-24 hours notice</span>
                        Up to 15% of booking price
                      </p>
                    </div>
                    <div style={styles.summaryItem}>
                      <span style={styles.summaryIcon}>🚨</span>
                      <p style={styles.summaryText}>
                        <span style={styles.summaryLabel}>Under 4 hours / driver dispatched</span>
                        Up to 33-50% of booking price
                      </p>
                    </div>
                  </div>
                </div>

                {/* Fee Structure Table */}
                <Card style={styles.sectionCard}>
                  <div style={styles.sectionHeader}>
                    <div style={styles.sectionNumber}>💰</div>
                    <h3 style={styles.sectionTitle}>Cancellation Fee Structure</h3>
                  </div>
                  <CardBody style={{ padding: 0 }}>
                    <div style={{overflowX: 'auto'}}>
                      <table style={styles.feeTable}>
                        <thead>
                          <tr>
                            <th style={styles.feeTableHeader}>When you cancel</th>
                            <th style={styles.feeTableHeader}>Fee</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td style={styles.feeTableCell}>
                              <strong>24+ hours</strong> before pickup
                            </td>
                            <td style={styles.feeTableCell}>
                              <span style={{...styles.feeBadge, ...styles.feeBadgeGreen}}>No fee</span>
                            </td>
                          </tr>
                          <tr>
                            <td style={styles.feeTableCellAlt}>
                              <strong>4-24 hours</strong> before pickup
                            </td>
                            <td style={styles.feeTableCellAlt}>
                              <span style={{...styles.feeBadge, ...styles.feeBadgeYellow}}>Up to 15%</span>
                            </td>
                          </tr>
                          <tr>
                            <td style={styles.feeTableCell}>
                              <strong>&lt;4 hours</strong> before pickup
                            </td>
                            <td style={styles.feeTableCell}>
                              <span style={{...styles.feeBadge, ...styles.feeBadgeOrange}}>Up to 33%</span>
                            </td>
                          </tr>
                          <tr>
                            <td style={styles.feeTableCellAlt}>
                              <strong>Driver dispatched/arrived</strong>
                            </td>
                            <td style={styles.feeTableCellAlt}>
                              <span style={{...styles.feeBadge, ...styles.feeBadgeRed}}>Up to 50%</span>
                              <br />
                              <small style={{ color: '#666', marginTop: '3px', display: 'block', fontSize: 'clamp(0.65rem, 2vw, 0.75rem)' }}>
                                + waiting charges
                              </small>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>

                {/* Table of Contents */}
                <div style={{ marginBottom: '25px' }}>
                  <h3 style={{ marginBottom: '12px', color: '#333', fontSize: 'clamp(1rem, 3.5vw, 1.2rem)' }}>Jump to section</h3>
                  <ul style={styles.tocList}>
                    <li><a href="#section-1" style={styles.tocItem}>1️⃣ Purpose of this Policy</a></li>
                    <li><a href="#section-2" style={styles.tocItem}>2️⃣ Quotes vs Confirmed Bookings</a></li>
                    <li><a href="#section-3" style={styles.tocItem}>3️⃣ When Cancellation Fees Apply</a></li>
                    <li><a href="#section-4" style={styles.tocItem}>4️⃣ Fee Structure Details</a></li>
                    <li><a href="#section-5" style={styles.tocItem}>5️⃣ Why Fees Exist</a></li>
                    <li><a href="#section-6" style={styles.tocItem}>6️⃣ Marketplace Allocation</a></li>
                    <li><a href="#section-7" style={styles.tocItem}>7️⃣ Customer No Show</a></li>
                    <li><a href="#section-8" style={styles.tocItem}>8️⃣ Driver No Show</a></li>
                    <li><a href="#section-9" style={styles.tocItem}>9️⃣ Exceptional Circumstances</a></li>
                    <li><a href="#section-10" style={styles.tocItem}>🔟 Staff Application Rules</a></li>
                    <li><a href="#section-11" style={styles.tocItem}>📢 Transparency Commitment</a></li>
                    <li><a href="#section-12" style={styles.tocItem}>❓ Disputes and Queries</a></li>
                  </ul>
                </div>

                {/* Section 1: Purpose */}
                <Card style={styles.sectionCard} id="section-1">
                  <div style={styles.sectionHeader}>
                    <span style={styles.sectionNumber}>1</span>
                    <h3 style={styles.sectionTitle}>Purpose of this Policy</h3>
                  </div>
                  <CardBody style={styles.sectionBody}>
                      <p>
                        This Cancellation Policy has been developed to provide clear,
                        fair, and transparent guidelines for all parties involved in
                        bookings made through the QikTruck platform. The policy is
                        designed to balance the interests of customers who may need to
                        change their plans with the legitimate expectations of drivers
                        who have committed their time and resources to fulfilling a
                        booking.
                      </p>
                      
                      <div style={styles.infoBox}>
                        <strong>📋 This policy explains:</strong>
                        <ul style={styles.contentList}>
                          <li style={styles.contentListItem}>When a booking becomes legally binding and creates obligations for both the customer and the allocated driver</li>
                          <li style={styles.contentListItem}>The specific circumstances under which a cancellation fee may be charged to compensate for losses incurred</li>
                          <li style={styles.contentListItem}>The methodology and fee bands used to calculate cancellation fees based on timing and booking progression</li>
                          <li style={styles.contentListItem}>The rationale behind cancellation fees and how they protect the fairness of the marketplace</li>
                          <li style={styles.contentListItem}>The guidelines that QikTruck staff must follow to ensure consistent and equitable application</li>
                        </ul>
                      </div>

                      <p>
                        QikTruck operates as a logistics marketplace that connects
                        customers with independent transport providers. When a booking
                        is confirmed, drivers commit their availability, reserve their
                        vehicles, and often decline other work opportunities in order
                        to fulfil the booking. Cancellation fees exist to fairly
                        compensate drivers for their time, lost earning opportunities,
                        and the platform resources invested in planning and coordinating
                        the service once a booking is confirmed.
                      </p>
                  </CardBody>
                </Card>

                {/* Section 2: Quotes vs Confirmed Bookings */}
                <Card style={styles.sectionCard} id="section-2">
                  <div style={styles.sectionHeader}>
                    <span style={styles.sectionNumber}>2</span>
                    <h3 style={styles.sectionTitle}>Quotes vs Confirmed Bookings</h3>
                  </div>
                  <CardBody style={styles.sectionBody}>
                    <p>
                        It is important to understand the distinction between a quote
                        and a confirmed booking, as this determines when obligations
                        arise and when cancellation fees may apply.
                      </p>

                      <Row className="mt-3">
                        <Col xs={12} md={6} className="mb-2">
                          <div style={{...styles.successBox, height: '100%', marginBottom: 0}}>
                            <h5 style={{color: '#155724', marginBottom: '10px', fontSize: 'clamp(0.9rem, 3vw, 1rem)'}}>✅ Quotes (FREE - No Obligation)</h5>
                            <p style={{fontSize: 'clamp(0.75rem, 2.5vw, 0.85rem)', lineHeight: '1.5'}}>
                              A quote is an estimate provided by the QikTruck platform based
                              on the information you supply, including pickup and delivery
                              locations, vehicle type, timing, and load details.
                            </p>
                            <ul style={{...styles.contentList, fontSize: 'clamp(0.75rem, 2.5vw, 0.85rem)'}}>
                              <li style={{marginBottom: '5px'}}>All quotes are completely free of charge</li>
                              <li style={{marginBottom: '5px'}}>No contractual relationship is created</li>
                              <li style={{marginBottom: '5px'}}>No cancellation fee ever applies to quotes</li>
                              <li style={{marginBottom: 0}}>Request as many quotes as you need</li>
                            </ul>
                          </div>
                        </Col>
                        <Col xs={12} md={6} className="mb-2">
                          <div style={{...styles.warningBox, height: '100%', marginBottom: 0}}>
                            <h5 style={{color: '#856404', marginBottom: '10px', fontSize: 'clamp(0.9rem, 3vw, 1rem)'}}>⚠️ Confirmed Bookings (Binding)</h5>
                            <p style={{fontSize: 'clamp(0.75rem, 2.5vw, 0.85rem)', lineHeight: '1.5'}}>
                              A booking becomes confirmed and legally binding only when <strong>ALL</strong> conditions are met:
                            </p>
                            <ul style={{...styles.contentList, fontSize: 'clamp(0.75rem, 2.5vw, 0.85rem)'}}>
                              <li style={{marginBottom: '5px'}}>Customer explicitly accepts the quoted price</li>
                              <li style={{marginBottom: '5px'}}>Booking confirmed via app, website, or writing</li>
                              <li style={{marginBottom: '5px'}}>This cancellation policy was shown/accessible</li>
                              <li style={{marginBottom: 0}}>Booking reference number issued</li>
                            </ul>
                          </div>
                        </Col>
                      </Row>

                      <div style={{...styles.infoBox, marginTop: '15px'}}>
                        <strong>🔒 Important:</strong> Without explicit customer confirmation meeting all criteria above, no cancellation fee may be charged. QikTruck will not impose fees where there is any ambiguity about whether the customer intended to confirm a binding booking.
                      </div>
                  </CardBody>
                </Card>

                {/* Section 3: When Cancellation Fees Apply */}
                <Card style={styles.sectionCard} id="section-3">
                  <div style={styles.sectionHeader}>
                    <span style={styles.sectionNumber}>3</span>
                    <h3 style={styles.sectionTitle}>When Cancellation Fees Apply</h3>
                  </div>
                  <CardBody style={styles.sectionBody}>
                    <p>
                        A cancellation fee may only be charged after a booking has been
                        confirmed AND at least one of the following operational steps has occurred:
                      </p>

                      <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginTop: '12px'}}>
                        <div style={{background: '#e7f3ff', border: '1px solid #b3d7ff', borderRadius: '8px', padding: '10px', textAlign: 'center'}}>
                          <div style={{fontSize: 'clamp(1.2rem, 4vw, 1.5rem)', marginBottom: '6px'}}>🚗</div>
                          <strong style={{fontSize: 'clamp(0.7rem, 2.2vw, 0.85rem)', display: 'block', marginBottom: '4px'}}>Driver Allocated</strong>
                          <p style={{fontSize: 'clamp(0.65rem, 2vw, 0.8rem)', marginBottom: 0, color: '#555', lineHeight: '1.3'}}>Driver assigned to the service</p>
                        </div>
                        <div style={{background: '#e7f3ff', border: '1px solid #b3d7ff', borderRadius: '8px', padding: '10px', textAlign: 'center'}}>
                          <div style={{fontSize: 'clamp(1.2rem, 4vw, 1.5rem)', marginBottom: '6px'}}>📅</div>
                          <strong style={{fontSize: 'clamp(0.7rem, 2.2vw, 0.85rem)', display: 'block', marginBottom: '4px'}}>Availability Reserved</strong>
                          <p style={{fontSize: 'clamp(0.65rem, 2vw, 0.8rem)', marginBottom: 0, color: '#555', lineHeight: '1.3'}}>Driver blocked from other work</p>
                        </div>
                        <div style={{background: '#e7f3ff', border: '1px solid #b3d7ff', borderRadius: '8px', padding: '10px', textAlign: 'center'}}>
                          <div style={{fontSize: 'clamp(1.2rem, 4vw, 1.5rem)', marginBottom: '6px'}}>🗺️</div>
                          <strong style={{fontSize: 'clamp(0.7rem, 2.2vw, 0.85rem)', display: 'block', marginBottom: '4px'}}>Route Planned</strong>
                          <p style={{fontSize: 'clamp(0.65rem, 2vw, 0.8rem)', marginBottom: 0, color: '#555', lineHeight: '1.3'}}>Scheduling completed</p>
                        </div>
                        <div style={{background: '#e7f3ff', border: '1px solid #b3d7ff', borderRadius: '8px', padding: '10px', textAlign: 'center'}}>
                          <div style={{fontSize: 'clamp(1.2rem, 4vw, 1.5rem)', marginBottom: '6px'}}>📱</div>
                          <strong style={{fontSize: 'clamp(0.7rem, 2.2vw, 0.85rem)', display: 'block', marginBottom: '4px'}}>Driver Notified</strong>
                          <p style={{fontSize: 'clamp(0.65rem, 2vw, 0.8rem)', marginBottom: 0, color: '#555', lineHeight: '1.3'}}>Driver preparing or dispatched</p>
                        </div>
                      </div>

                      <div style={{...styles.successBox, marginTop: '15px'}}>
                        <strong>✅ No Resources Committed = No Fee:</strong><br/>
                        If none of the above operational steps have occurred at the time of cancellation, the fee must be substantially reduced or waived entirely.
                      </div>
                  </CardBody>
                </Card>

                {/* Section 4: Fee Structure */}
                <Card style={styles.sectionCard} id="section-4">
                  <div style={styles.sectionHeader}>
                    <span style={styles.sectionNumber}>4</span>
                    <h3 style={styles.sectionTitle}>Cancellation Fee Structure</h3>
                  </div>
                  <CardBody style={styles.sectionBody}>
                    <p>
                        QikTruck is committed to ensuring that cancellation fees are
                        always reasonable, proportional, and directly related to the
                        actual costs incurred. Fees are not penalties but fair compensation for demonstrable losses.
                      </p>

                      <div style={styles.infoBox}>
                        <strong>💡 How Fees Are Calculated:</strong> The exact fee charged depends on how far the booking has progressed through operational stages. QikTruck will always apply the lowest reasonable fee consistent with the circumstances.
                      </div>

                      <div style={{overflowX: 'auto', marginTop: '12px'}}>
                        <table style={styles.feeTable}>
                          <thead>
                            <tr>
                              <th style={styles.feeTableHeader}>Timing</th>
                              <th style={styles.feeTableHeader}>Max Fee</th>
                              <th style={styles.feeTableHeader}>Rationale</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td style={styles.feeTableCell}><strong>24+ hrs</strong> before</td>
                              <td style={styles.feeTableCell}><span style={styles.feeBadgeFree}>FREE</span></td>
                              <td style={styles.feeTableCell}>Driver can find other work</td>
                            </tr>
                            <tr>
                              <td style={styles.feeTableCellAlt}><strong>4-24 hrs</strong> before</td>
                              <td style={styles.feeTableCellAlt}><span style={styles.feeBadgeLow}>15%</span></td>
                              <td style={styles.feeTableCellAlt}>Lost opportunity; resources used</td>
                            </tr>
                            <tr>
                              <td style={styles.feeTableCell}><strong>&lt;4 hrs</strong> before</td>
                              <td style={styles.feeTableCell}><span style={styles.feeBadgeMedium}>33%</span></td>
                              <td style={styles.feeTableCell}>Limited replacement chance</td>
                            </tr>
                            <tr>
                              <td style={styles.feeTableCellAlt}><strong>Dispatched</strong> / arrived</td>
                              <td style={styles.feeTableCellAlt}><span style={styles.feeBadgeHigh}>50%</span></td>
                              <td style={styles.feeTableCellAlt}>Time, fuel + waiting fees</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                  </CardBody>
                </Card>

                {/* Section 5: Why Fees Exist */}
                <Card style={styles.sectionCard} id="section-5">
                  <div style={styles.sectionHeader}>
                    <span style={styles.sectionNumber}>5</span>
                    <h3 style={styles.sectionTitle}>Why Cancellation Fees Exist</h3>
                  </div>
                  <CardBody style={styles.sectionBody}>
                    <p>
                        Cancellation fees are an essential part of maintaining a fair
                        and sustainable marketplace for all participants:
                      </p>

                      <Row className="mt-2">
                        <Col xs={6} className="mb-2">
                          <div style={{padding: '10px', background: '#f8f9fa', borderRadius: '8px', height: '100%', borderLeft: '3px solid #FF6B00'}}>
                            <strong style={{color: '#333', fontSize: 'clamp(0.7rem, 2.2vw, 0.85rem)'}}>⏰ Lost Earnings</strong>
                            <p style={{fontSize: 'clamp(0.65rem, 2vw, 0.8rem)', marginTop: '6px', marginBottom: 0, lineHeight: '1.4', color: '#666'}}>
                              Drivers turn down other work when they accept a booking.
                            </p>
                          </div>
                        </Col>
                        <Col xs={6} className="mb-2">
                          <div style={{padding: '10px', background: '#f8f9fa', borderRadius: '8px', height: '100%', borderLeft: '3px solid #FF6B00'}}>
                            <strong style={{color: '#333', fontSize: 'clamp(0.7rem, 2.2vw, 0.85rem)'}}>🚛 Vehicle Reserved</strong>
                            <p style={{fontSize: 'clamp(0.65rem, 2vw, 0.8rem)', marginTop: '6px', marginBottom: 0, lineHeight: '1.4', color: '#666'}}>
                              Vehicles are blocked from other work during booking.
                            </p>
                          </div>
                        </Col>
                        <Col xs={6} className="mb-2">
                          <div style={{padding: '10px', background: '#f8f9fa', borderRadius: '8px', height: '100%', borderLeft: '3px solid #FF6B00'}}>
                            <strong style={{color: '#333', fontSize: 'clamp(0.7rem, 2.2vw, 0.85rem)'}}>📊 Resources Used</strong>
                            <p style={{fontSize: 'clamp(0.65rem, 2vw, 0.8rem)', marginTop: '6px', marginBottom: 0, lineHeight: '1.4', color: '#666'}}>
                              Scheduling and coordination resources invested.
                            </p>
                          </div>
                        </Col>
                        <Col xs={6} className="mb-2">
                          <div style={{padding: '10px', background: '#f8f9fa', borderRadius: '8px', height: '100%', borderLeft: '3px solid #FF6B00'}}>
                            <strong style={{color: '#333', fontSize: 'clamp(0.7rem, 2.2vw, 0.85rem)'}}>⚖️ Driver Fairness</strong>
                            <p style={{fontSize: 'clamp(0.65rem, 2vw, 0.8rem)', marginTop: '6px', marginBottom: 0, lineHeight: '1.4', color: '#666'}}>
                              Protects drivers from bearing full cost of changes.
                            </p>
                          </div>
                        </Col>
                      </Row>

                      <div style={{...styles.infoBox, marginTop: '10px'}}>
                        <strong>💡 Remember:</strong> Cancellation fees are NOT penalties. They are fair compensation for real costs incurred and genuine opportunities lost when a confirmed booking is cancelled.
                      </div>
                  </CardBody>
                </Card>

                {/* Section 6: Marketplace Allocation */}
                <Card style={styles.sectionCard} id="section-6">
                  <div style={styles.sectionHeader}>
                    <span style={styles.sectionNumber}>6</span>
                    <h3 style={styles.sectionTitle}>Marketplace Allocation Rule</h3>
                  </div>
                  <CardBody style={styles.sectionBody}>
                    <p>
                        QikTruck operates as a marketplace connecting customers with
                        independent transport providers. Cancellation fees are allocated as follows:
                      </p>

                      <div style={{display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: '#f8f9fa', borderRadius: '8px'}}>
                          <span style={{fontSize: 'clamp(1.2rem, 4vw, 1.5rem)', flexShrink: 0}}>🚗</span>
                          <div>
                            <strong style={{fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)'}}>Driver Portion</strong>
                            <p style={{marginBottom: 0, fontSize: 'clamp(0.7rem, 2.2vw, 0.8rem)', color: '#666', lineHeight: '1.4'}}>Compensates for lost time and opportunity cost</p>
                          </div>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: '#f8f9fa', borderRadius: '8px'}}>
                          <span style={{fontSize: 'clamp(1.2rem, 4vw, 1.5rem)', flexShrink: 0}}>🏢</span>
                          <div>
                            <strong style={{fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)'}}>Platform Portion</strong>
                            <p style={{marginBottom: 0, fontSize: 'clamp(0.7rem, 2.2vw, 0.8rem)', color: '#666', lineHeight: '1.4'}}>Covers payment processing and support costs</p>
                          </div>
                        </div>
                      </div>

                      <div style={{...styles.infoBox, marginTop: '15px'}}>
                        The specific allocation between driver and QikTruck is handled according to driver compensation rules and partnership agreements, designed to ensure drivers receive fair compensation.
                      </div>
                  </CardBody>
                </Card>

                {/* Section 7: Customer No Show */}
                <Card style={styles.sectionCard} id="section-7">
                  <div style={styles.sectionHeader}>
                    <span style={styles.sectionNumber}>7</span>
                    <h3 style={styles.sectionTitle}>Customer No Show</h3>
                  </div>
                  <CardBody style={styles.sectionBody}>
                    <p>
                        A customer no show occurs when the driver arrives at the
                        scheduled pickup location but the customer is not present or cannot be contacted.
                      </p>

                      <div style={{background: '#f8f9fa', padding: '12px', borderRadius: '8px', marginTop: '12px', borderLeft: '3px solid #FF6B00'}}>
                        <h5 style={{color: '#333', marginBottom: '10px', fontSize: 'clamp(0.85rem, 2.8vw, 1rem)'}}>⏱️ No Show Process</h5>
                        <ol style={{marginBottom: 0, paddingLeft: '18px', fontSize: 'clamp(0.75rem, 2.5vw, 0.85rem)', lineHeight: '1.5', color: '#666'}}>
                          <li style={{marginBottom: '8px'}}>
                            <strong style={{color: '#333'}}>15-minute grace period</strong> — driver waits and attempts contact
                          </li>
                          <li style={{marginBottom: '8px'}}>
                            <strong style={{color: '#333'}}>Waiting charges</strong> may apply for additional waiting
                          </li>
                          <li style={{marginBottom: '8px'}}>
                            If unavailable, treated as <strong style={{color: '#333'}}>late cancellation</strong>
                          </li>
                          <li style={{marginBottom: 0}}>
                            Fees at <strong style={{color: '#333'}}>"driver arrived"</strong> rate (up to 50%)
                          </li>
                        </ol>
                      </div>
                  </CardBody>
                </Card>

                {/* Section 8: Driver No Show */}
                <Card style={styles.sectionCard} id="section-8">
                  <div style={styles.sectionHeader}>
                    <span style={styles.sectionNumber}>8</span>
                    <h3 style={styles.sectionTitle}>Driver No Show or Driver Cancellation</h3>
                  </div>
                  <CardBody style={styles.sectionBody}>
                    <p>
                        QikTruck holds drivers to high standards of reliability.
                        If a driver fails to fulfil a confirmed booking, customers are protected:
                      </p>

                      <Row className="mt-2">
                        <Col xs={12} md={5} className="mb-2">
                          <div style={{...styles.warningBox, height: '100%', marginBottom: 0}}>
                            <h6 style={{color: '#856404', fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)', marginBottom: '8px'}}>Driver Fails When:</h6>
                            <ul style={{...styles.contentList, marginBottom: 0, fontSize: 'clamp(0.75rem, 2.2vw, 0.85rem)'}}>
                              <li style={{marginBottom: '5px'}}>Does not arrive without notice</li>
                              <li style={{marginBottom: '5px'}}>Cancels without valid reason</li>
                              <li style={{marginBottom: 0}}>Unable to perform (their fault)</li>
                            </ul>
                          </div>
                        </Col>
                        <Col xs={12} md={7}>
                          <div style={{...styles.successBox, height: '100%', marginBottom: 0}}>
                            <h6 style={{color: '#155724', fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)', marginBottom: '8px'}}>✅ Customer Protections:</h6>
                            <ul style={{...styles.contentList, marginBottom: 0, fontSize: 'clamp(0.75rem, 2.2vw, 0.85rem)'}}>
                              <li style={{marginBottom: '5px'}}><strong>No fee</strong> charged to customer</li>
                              <li style={{marginBottom: '5px'}}>QikTruck assigns <strong>replacement driver</strong></li>
                              <li style={{marginBottom: 0}}>If no replacement: <strong>full refund</strong></li>
                            </ul>
                          </div>
                        </Col>
                      </Row>
                  </CardBody>
                </Card>

                {/* Section 9: Exceptional Circumstances */}
                <Card style={styles.sectionCard} id="section-9">
                  <div style={styles.sectionHeader}>
                    <span style={styles.sectionNumber}>9</span>
                    <h3 style={styles.sectionTitle}>Exceptional Circumstances</h3>
                  </div>
                  <CardBody style={styles.sectionBody}>
                    <p>
                        QikTruck recognises situations where strict application of cancellation
                        fees would be unfair. Fees may be reduced or waived in these verified circumstances:
                      </p>

                      <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginTop: '12px'}}>
                        <div style={{textAlign: 'center', padding: '10px', background: '#f8f9fa', borderRadius: '8px'}}>
                          <span style={{fontSize: 'clamp(1.3rem, 4vw, 1.8rem)'}}>🏥</span>
                          <h6 style={{marginTop: '6px', marginBottom: '3px', fontSize: 'clamp(0.75rem, 2.2vw, 0.85rem)'}}>Medical</h6>
                          <p style={{fontSize: 'clamp(0.65rem, 2vw, 0.75rem)', color: '#666', marginBottom: 0, lineHeight: '1.3'}}>Emergency affecting you or family</p>
                        </div>
                        <div style={{textAlign: 'center', padding: '10px', background: '#f8f9fa', borderRadius: '8px'}}>
                          <span style={{fontSize: 'clamp(1.3rem, 4vw, 1.8rem)'}}>⚠️</span>
                          <h6 style={{marginTop: '6px', marginBottom: '3px', fontSize: 'clamp(0.75rem, 2.2vw, 0.85rem)'}}>Safety</h6>
                          <p style={{fontSize: 'clamp(0.65rem, 2vw, 0.75rem)', color: '#666', marginBottom: 0, lineHeight: '1.3'}}>Genuine security concerns</p>
                        </div>
                        <div style={{textAlign: 'center', padding: '10px', background: '#f8f9fa', borderRadius: '8px'}}>
                          <span style={{fontSize: 'clamp(1.3rem, 4vw, 1.8rem)'}}>🔧</span>
                          <h6 style={{marginTop: '6px', marginBottom: '3px', fontSize: 'clamp(0.75rem, 2.2vw, 0.85rem)'}}>Breakdown</h6>
                          <p style={{fontSize: 'clamp(0.65rem, 2vw, 0.75rem)', color: '#666', marginBottom: 0, lineHeight: '1.3'}}>Major vehicle breakdown</p>
                        </div>
                        <div style={{textAlign: 'center', padding: '10px', background: '#f8f9fa', borderRadius: '8px'}}>
                          <span style={{fontSize: 'clamp(1.3rem, 4vw, 1.8rem)'}}>🌪️</span>
                          <h6 style={{marginTop: '6px', marginBottom: '3px', fontSize: 'clamp(0.75rem, 2.2vw, 0.85rem)'}}>Force Majeure</h6>
                          <p style={{fontSize: 'clamp(0.65rem, 2vw, 0.75rem)', color: '#666', marginBottom: 0, lineHeight: '1.3'}}>Disasters, weather, unrest</p>
                        </div>
                      </div>

                    <div style={{...styles.infoBox, marginTop: '15px'}}>
                      <strong>📞 How to Request a Waiver:</strong> Contact QikTruck support as soon as possible with relevant documentation or evidence. All decisions are logged and documented for consistency.
                    </div>
                  </CardBody>
                </Card>

                {/* Section 10: Staff Rules */}
                <Card style={styles.sectionCard} id="section-10">
                  <div style={styles.sectionHeader}>
                    <span style={styles.sectionNumber}>10</span>
                    <h3 style={styles.sectionTitle}>Staff Application Rules</h3>
                  </div>
                  <CardBody style={styles.sectionBody}>
                      <p>
                        QikTruck staff and support teams follow these guidelines to apply
                        this policy consistently and fairly:
                      </p>

                      <div style={{background: '#f8f9fa', padding: '12px', borderRadius: '8px', marginTop: '12px'}}>
                        <ol style={{marginBottom: 0, paddingLeft: '18px', fontSize: 'clamp(0.75rem, 2.5vw, 0.85rem)', lineHeight: '1.5'}}>
                          <li style={{marginBottom: '10px'}}>
                            <strong>Verify Acceptance:</strong> Confirm customer accepted (button click, written acceptance)
                          </li>
                          <li style={{marginBottom: '10px'}}>
                            <strong>Check Records:</strong> Review driver allocation and dispatch logs
                          </li>
                          <li style={{marginBottom: '10px'}}>
                            <strong>Lowest Fee:</strong> When uncertain, apply the lowest reasonable fee
                          </li>
                          <li style={{marginBottom: 0}}>
                            <strong>Document:</strong> Record all decisions and rationale
                          </li>
                        </ol>
                      </div>

                    <div style={{...styles.warningBox, marginTop: '12px'}}>
                      <strong>⚖️ Rule:</strong> No acceptance + No allocation = <strong>No fee</strong>. Both conditions required.
                    </div>
                  </CardBody>
                </Card>

                {/* Section 11: Transparency */}
                <Card style={styles.sectionCard} id="section-11">
                  <div style={styles.sectionHeader}>
                    <span style={styles.sectionNumber}>11</span>
                    <h3 style={styles.sectionTitle}>Transparency Commitment</h3>
                  </div>
                  <CardBody style={styles.sectionBody}>
                      <p>
                        QikTruck is committed to operating a transparent and fair marketplace:
                      </p>

                      <div style={{display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', background: '#f8f9fa', borderRadius: '8px', borderLeft: '3px solid #FF6B00'}}>
                          <span style={{fontSize: 'clamp(1rem, 3vw, 1.3rem)', flexShrink: 0}}>👁️</span>
                          <div>
                            <strong style={{fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)'}}>Clear Display</strong>
                            <p style={{marginBottom: 0, fontSize: 'clamp(0.7rem, 2.2vw, 0.8rem)', color: '#666', lineHeight: '1.4'}}>Policy shown before booking confirmation</p>
                          </div>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', background: '#f8f9fa', borderRadius: '8px', borderLeft: '3px solid #FF6B00'}}>
                          <span style={{fontSize: 'clamp(1rem, 3vw, 1.3rem)', flexShrink: 0}}>📋</span>
                          <div>
                            <strong style={{fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)'}}>Clear Indication</strong>
                            <p style={{marginBottom: 0, fontSize: 'clamp(0.7rem, 2.2vw, 0.8rem)', color: '#666', lineHeight: '1.4'}}>Shows when booking becomes binding</p>
                          </div>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', background: '#f8f9fa', borderRadius: '8px', borderLeft: '3px solid #FF6B00'}}>
                          <span style={{fontSize: 'clamp(1rem, 3vw, 1.3rem)', flexShrink: 0}}>🔄</span>
                          <div>
                            <strong style={{fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)'}}>Consistent</strong>
                            <p style={{marginBottom: 0, fontSize: 'clamp(0.7rem, 2.2vw, 0.8rem)', color: '#666', lineHeight: '1.4'}}>Same policy across all channels</p>
                          </div>
                        </div>
                      </div>
                  </CardBody>
                </Card>

                {/* Section 12: Disputes */}
                <Card style={styles.sectionCard} id="section-12">
                  <div style={styles.sectionHeader}>
                    <span style={styles.sectionNumber}>12</span>
                    <h3 style={styles.sectionTitle}>Disputes and Queries</h3>
                  </div>
                  <CardBody style={styles.sectionBody}>
                      <p>
                        If you believe a cancellation fee has been applied incorrectly
                        or unfairly, or if you have any questions about this policy:
                      </p>

                    <div style={{background: '#f8f9fa', padding: '15px 12px', borderRadius: '10px', marginTop: '12px', textAlign: 'center', border: '1px solid #e9ecef'}}>
                      <span style={{fontSize: 'clamp(1.8rem, 6vw, 2.5rem)'}}>📞</span>
                      <h5 style={{marginTop: '8px', marginBottom: '6px', fontSize: 'clamp(0.9rem, 3vw, 1rem)', color: '#FF6B00'}}>Contact QikTruck Support</h5>
                      <p style={{marginBottom: '8px', color: '#555', fontSize: 'clamp(0.75rem, 2.5vw, 0.85rem)', lineHeight: '1.4'}}>
                        All disputes reviewed fairly and promptly.
                      </p>
                      <p style={{marginBottom: 0, fontSize: 'clamp(0.7rem, 2.2vw, 0.8rem)', color: '#666'}}>
                        See our <a href="/terms">Terms</a> for dispute procedures.
                      </p>
                    </div>
                  </CardBody>
                </Card>

                {/* Related Policies */}
                <Card style={{...styles.sectionCard, background: '#f8f9fa', border: '1px solid #e9ecef'}}>
                  <CardBody>
                    <div style={{textAlign: 'center', padding: '10px'}}>
                      <h4 style={{marginBottom: '10px', fontSize: 'clamp(0.95rem, 3vw, 1.1rem)'}}>📚 Related Policies</h4>
                      <p style={{marginBottom: '12px', color: '#555', fontSize: 'clamp(0.75rem, 2.5vw, 0.85rem)'}}>
                        Part of QikTruck's terms of service.
                      </p>
                      <div style={{display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap'}}>
                        <a href="/terms" style={{display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '8px 12px', background: '#fff', borderRadius: '6px', textDecoration: 'none', color: '#333', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', fontSize: 'clamp(0.75rem, 2.5vw, 0.85rem)'}}>
                          📜 Terms
                        </a>
                        <a href="/privacy" style={{display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '8px 12px', background: '#fff', borderRadius: '6px', textDecoration: 'none', color: '#333', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', fontSize: 'clamp(0.75rem, 2.5vw, 0.85rem)'}}>
                          🔒 Privacy
                        </a>
                      </div>
                    </div>
                  </CardBody>
                </Card>

              </Col>
            </Row>
          </Container>
        </section>
      </React.Fragment>
    );
  }
}

export default Section;
